import { createStyles, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import {
  backgroundThemes,
  BasePageQuotation,
  Footer,
  HeaderWithPercentBar,
  NetworkIndicator,
  themeMethods,
} from 'design-system';
import queryString from 'query-string';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
// data layer
import { Quotation, RequestIndicatorTypes } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
// store & redux
import { connect, ConnectedProps } from 'react-redux';
import {
  repopulateRedux,
  requestEmissionCreation,
} from 'src/store/emissionPolicy/actions';
import { REQUEST_PERSISTED_DATA } from 'src/store/emissionPolicy/requestIdentifiers';
import { RootState } from 'src/store/rootReducer';
// constants
import { profiles } from 'src/constants/profileTypes';
// functions
import getProgressPercent from 'src/functions/getProgressPercent';
// components
import { getCurrentQuotation } from 'src/store/quotation/actions';
import { REQUEST_QUOTATION } from 'src/store/quotation/requestIdentifiers';
import DataForPayment from '../../Icons/DataForPayment';
import { NetworkProgressDialogue } from '../components';
import Confirmation from '../EmissionPolicy/Confirmation';
import PaymentData from '../EmissionPolicy/PaymentData';
import { EmissionErrorHandler } from './components';
import ErrorIssuedLayout from '../components/ErrorIssuedLayout';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
    },
    footer: {
      marginTop: 'auto',
    },
    container: {
      flexGrow: 1,
    },
    showBackgroung: {
      backgroundSize: '100%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'bottom',
      background: `linear-gradient(359.66deg, rgba(242, 244, 243,0.4) 13.26%, rgba(255, 255, 255,0.4) 88.97%), 
        ${themeMethods.getCurrentMonthTheme(
          new Date(),
          backgroundThemes.QUOTATION_PAGE,
        )}`,
      [theme.breakpoints.down('sm')]: {
        background: 'none',
      },
    },
  }),
);

export const ContinueEmission = ({
  quotation,
  emissionRequestIndicator,
  requestEmissionCreationAction,
  requestPersistedDataAction,
  requestGetQuotation,
}: PropsFromRedux): JSX.Element => {
  const classes = useStyles();
  const location = useLocation();
  const values = queryString.parse(location.search);
  const uniqueId: string = values.uniqueId?.toString() || '';
  const accepted: string = values.accepted?.toString() || '';
  const hash: string = quotation.urlSalt || '';
  const requestPersistStatus = useNetworkObserver(REQUEST_PERSISTED_DATA);
  const requestGetQuotationStatus = useNetworkObserver(REQUEST_QUOTATION);

  const isCollectiveProfile = useMemo(
    () => quotation.quotationProfile.type === profiles.COLLECTIVE,
    [quotation],
  );

  const showError = useMemo(
    () =>
      accepted !== 'True' ||
      [requestPersistStatus, emissionRequestIndicator].includes('failure'),
    [accepted, requestPersistStatus, emissionRequestIndicator],
  );

  const showRequest = useMemo(
    () => [requestPersistStatus].includes('requesting'),
    [requestPersistStatus],
  );

  const showIssuedError = useMemo(() => quotation.status.id === 4, [quotation]);

  const showSuccess = useMemo(
    () => emissionRequestIndicator === 'success',
    [emissionRequestIndicator],
  );

  const showPayment = useMemo(
    () =>
      !isCollectiveProfile &&
      requestPersistStatus === 'success' &&
      emissionRequestIndicator === 'initial' &&
      requestGetQuotationStatus === 'success' &&
      !showIssuedError,
    [
      isCollectiveProfile,
      requestPersistStatus,
      emissionRequestIndicator,
      requestGetQuotationStatus,
    ],
  );

  useEffect(() => {
    if (accepted === 'True') {
      requestPersistedDataAction(uniqueId);
    }
  }, [requestPersistedDataAction, uniqueId, accepted]);

  useEffect(() => {
    if (requestPersistStatus === 'success' && isCollectiveProfile) {
      requestEmissionCreationAction();
    }

    if (requestPersistStatus === 'success' && !isCollectiveProfile) {
      requestGetQuotation(hash, quotation.packageOption);
    }
  }, [
    requestEmissionCreationAction,
    requestPersistStatus,
    isCollectiveProfile,
    requestGetQuotation,
    hash,
    quotation.packageOption,
  ]);

  const handlePayment = (): void => {
    requestEmissionCreationAction();
  };

  const customerCurrentName =
    quotation.customerType === 'J'
      ? String(quotation.legalEntity?.name)
      : `${quotation.customer.firstName} ${quotation.customer.lastName}`;

  return (
    <>
      <div className={classes.root}>
        <div
          className={clsx(classes.container, {
            [classes.showBackgroung]: !(showError || showIssuedError),
          })}
        >
          <BasePageQuotation
            data-testid="main-content"
            rightActions={null}
            backgroundColor="white"
            stepperItem={
              showPayment && (
                <HeaderWithPercentBar
                  data-testid="stepper"
                  goToBackPage={(): void => {}}
                  icon={<DataForPayment />}
                  stepperTitle="Datos del pago"
                  currentProgress={getProgressPercent(5, 6)}
                />
              )
            }
          >
            <NetworkIndicator
              data-testid="network-indicator"
              message="Obteniendo datos de cotización..."
              requesting={showRequest}
            />

            {showSuccess && (
              <Confirmation data-testid="emission-confirmation" />
            )}

            {showIssuedError && (
              <ErrorIssuedLayout
                customerName={customerCurrentName}
                data-testid="emission-issued-error"
              />
            )}

            {showPayment && (
              <PaymentData
                data-testid="payment-data"
                onSubmit={handlePayment}
                accepted={accepted}
                uniqueId={uniqueId}
              />
            )}

            {showError && <EmissionErrorHandler data-testid="emission-error" />}

            <NetworkProgressDialogue
              data-testid="network-dialogue"
              open={emissionRequestIndicator === 'requesting'}
              mainText="Espera un momento"
              caption="Estamos emitiendo tu póliza."
              comment="(no cierres esta ventana)"
            />
          </BasePageQuotation>
        </div>
        <footer data-testid="footer" className={classes.footer}>
          <Footer
            country="Costa Rica"
            linkText="Consulte nuestros términos y condiciones"
            linkHref="https://www.assanet.cr/condiciones-generales/#seguros-generales"
          />
        </footer>
      </div>
    </>
  );
};
export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  emissionRequestIndicator: RequestIndicatorTypes;
} => ({
  quotation: state.quotation.quotation,
  emissionRequestIndicator: state.emissionPolicy.emissionRequestIndicator,
});
const reduxActions = {
  requestEmissionCreationAction: requestEmissionCreation,
  requestPersistedDataAction: repopulateRedux,
  requestGetQuotation: getCurrentQuotation,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(ContinueEmission);
